import Core               from "@/core/Core";
import BankAccount        from "./BankAccount";
import BankAccountsDAO    from "./BankAccountsDAO";
import Partner            from "@/domains/Partners/Partner";
import Company            from "@/domains/Companies/Company";
import ContextMenuItem    from "@/lib/components/VContextMenu/ContextMenuItem";

/**
 * Сервис для работы с расчётными счетами
 */
class BankAccountsService {

  static async get(id: number): Promise<BankAccount | null> {
    const response = await BankAccountsDAO.get(id);

    return Core.utils.hydrate(response.data, BankAccount.fromServer);
  }

  static async getList(companyId: number): Promise<BankAccount[]> {
    const response = await BankAccountsDAO.getList(companyId);

    return Core.utils.hydrateArray(response.data, BankAccount.fromServer);
  }

  /** ------- CRUD ------- */

  static async add(companyId: number, bankAccount: BankAccount): Promise<boolean> {
    const response = await BankAccountsDAO.add(companyId, bankAccount);

    if (response.ok) {
      Core.showNotification.success('Расчётный счёт успешно добавлен');
    }

    return response.ok;
  }

  static async delete(bankAccount: BankAccount): Promise<boolean> {
    const response = await BankAccountsDAO.delete(bankAccount.id);

    if (response.ok) {
      Core.showNotification.success('Расчётный счёт успешно удалён');
    }

    return response.ok;
  }

  static async setMain(bankAccount: BankAccount): Promise<boolean> {
    const response = await BankAccountsDAO.setMain(bankAccount.id);

    if (response.ok) {
      Core.showNotification.success('Расчётный счёт установлен как основной');
    }

    return response.ok;
  }

  /**
   * Удаление с подтверждением. Используется из контекстного меню счёта.
   * Возвращает true если удаление произошло.
   */
  static async deleteWithConfirmation(bankAccount: BankAccount): Promise<boolean> {
    const confirmed = await Core.dialogs.confirm(
      'Удалить этот расчётный счёт?',
      Core.utils.regional.ru.formatBankAccount(bankAccount.account),
    );

    if (confirmed === true) {
      return await this.delete(bankAccount);
    } else {
      return false;
    }
  }

  /** ------ открытие диалога создания/просмотра расчётного счёта ------ */

  /**
   * Карточка расчётного счёта. bankAccount === null — создание нового счёта
   * партнёра, company — юр. лицо, к которому его сразу привязать.
   * Существующий счёт карточка только показывает: редактировать его нельзя.
   */
  static showBankAccountCard(
    bankAccount: BankAccount | null,
    partner: Partner,
    company: Company | null = null,
  ): Promise<boolean> {
    return Core.dialogs.BankAccountCard.showAsPromise({ bankAccount, partner, company });
  }

  // ============================================================
  // Контекстное меню
  // ============================================================

  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Сделать основным',
        'verified',
        (bankAccount: BankAccount) => { return BankAccountsService.setMain(bankAccount); },
        (bankAccount: BankAccount) => bankAccount.getReasonCannotSetMain(),
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        (bankAccount: BankAccount) => { return BankAccountsService.deleteWithConfirmation(bankAccount); },
      ),
    ];
  }
}

export default BankAccountsService;